import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _BaseQuizInfo;
function _callSuper(_this, derived, args) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
      return !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    } catch (e) {
      return false;
    }
  }
  derived = _getPrototypeOf(derived);
  return _possibleConstructorReturn(_this, isNativeReflectConstruct() ? Reflect.construct(derived, args || [], _getPrototypeOf(_this).constructor) : derived.apply(_this, args));
}
/** @jsx jsx */
import { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import isAfter from 'date-fns/is_after';
import { IconWarningLine } from '@instructure/ui-icons';
import { jsx } from '@instructure/emotion';
import { AccessibleContent, PresentationContent } from '@instructure/ui-a11y-content';
import t from '@instructure/quiz-i18n/format-message';
import { withI18nSupport, withStyleOverrides } from '@instructure/quiz-common';
import { FormattedTimer as FormattedDuration, formatTime } from '@instructure/quiz-core';
import { InfoBox } from './InfoBox';
import generateStyle from './styles';
import generateComponentTheme from './theme';
export var BaseQuizInfo = /*#__PURE__*/function (_withI18nSupport) {
  function BaseQuizInfo() {
    _classCallCheck(this, BaseQuizInfo);
    return _callSuper(this, BaseQuizInfo, arguments);
  }
  _inherits(BaseQuizInfo, _withI18nSupport);
  return _createClass(BaseQuizInfo, [{
    key: "dueAt",
    get: function get() {
      if (!this.props.quizSession || !this.props.quizSession.dueAt) {
        return null;
      }
      return this.props.quizSession.dueAt;
    }
  }, {
    key: "timeLimitInSeconds",
    get: function get() {
      var _this$props = this.props,
        quiz = _this$props.quiz,
        quizSession = _this$props.quizSession;
      if (quizSession) {
        if (quizSession.disableTimer) {
          return 0;
        }
        if (quizSession.hasTimeAllowed) {
          return quizSession.timeAllowed();
        }
      }
      var quizTimeLimit = quiz.getTimeLimit();
      if (quizTimeLimit === 0) {
        return 0;
      }
      if (quizSession) {
        var timerMultiplier = quizSession.get('timerMultiplier') || 1;
        var extraTimeInSeconds = quizSession.get('extraTimeInSeconds') || 0;
        return quizTimeLimit * timerMultiplier + extraTimeInSeconds;
      }
      return quizTimeLimit;
    }
  }, {
    key: "hasTimeLimit",
    get: function get() {
      return this.timeLimitInSeconds > 0;
    }
  }, {
    key: "hasDueAt",
    get: function get() {
      return this.dueAt !== null;
    }
  }, {
    key: "hasInfoBoxContent",
    get: function get() {
      return this.hasTimeLimit || this.hasDueAt;
    }
  }, {
    key: "renderTimeLimitPrimary",
    value: function renderTimeLimitPrimary() {
      if (!this.hasTimeLimit) {
        return jsx(AccessibleContent, {
          alt: t('No Time Limit'),
          "data-automation": "sdk-show-timelimit-none"
        }, t({
          "default": '--:--',
          description: 'Placeholder for no time limit'
        }));
      }
      var alt = t('Time limit: { limit }. During the quiz, press Alt+Shift+T to have the remaining time read', {
        limit: formatTime(this.timeLimitInSeconds)
      });
      return jsx(AccessibleContent, {
        alt: alt,
        "data-automation": "sdk-show-timelimit"
      }, jsx(FormattedDuration, {
        fontSize: "xx-large",
        seconds: this.timeLimitInSeconds
      }));
    }
  }, {
    key: "renderTimeLimitSecondary",
    value: function renderTimeLimitSecondary() {
      var text = this.hasTimeLimit ? t('Time Limit') : t('No Time Limit');
      return jsx(PresentationContent, null, text);
    }
  }, {
    key: "renderDueDatePrimary",
    value: function renderDueDatePrimary(d) {
      if (!this.hasDueAt) {
        return jsx(AccessibleContent, {
          alt: t('No Due Date')
        }, '--' /* eslint-disable-line react/jsx-no-literals */);
      }
      var dateTime = "".concat(this.formatDateShort(d), " ").concat(this.formatTimeHourMinute(d));
      return jsx(AccessibleContent, {
        alt: t('Due on { dateTime }', {
          dateTime: dateTime
        })
      }, dateTime);
    }
  }, {
    key: "renderDueDateSecondary",
    value: function renderDueDateSecondary(d) {
      if (!this.hasDueAt) {
        return jsx(PresentationContent, null, t('No Due Date'));
      }
      var daysUntil = differenceInCalendarDays(d, Date.now());
      if (isAfter(Date.now(), d)) {
        return this.props.hasSubmission ? null : jsx("div", {
          css: this.props.styles.pastDue
        }, jsx("div", {
          css: this.props.styles.iconWrapper
        }, jsx(IconWarningLine, null)), t("Past Due: {\n              daysUntil, plural,\n              one {# day}\n              other {# days}\n            }", {
          daysUntil: Math.abs(daysUntil)
        }));
      }
      return t("Due in {\n      daysUntil, plural,\n      one {# day}\n      other {# days}\n    }", {
        daysUntil: daysUntil
      });
    }
  }, {
    key: "renderTimeLimit",
    value: function renderTimeLimit() {
      var accessibilityText;
      if (this.hasTimeLimit) {
        accessibilityText = t('Time Limit {time}', {
          time: formatTime(this.timeLimitInSeconds)
        });
      } else if (!this.props.styles.showEmptyInfoBox) {
        return null;
      }
      return jsx(InfoBox, {
        accessibilityText: accessibilityText,
        mainText: this.renderTimeLimitPrimary(),
        subText: this.renderTimeLimitSecondary()
      });
    }
  }, {
    key: "renderDueDate",
    value: function renderDueDate() {
      if (!this.props.styles.showEmptyInfoBox && !this.hasDueAt) {
        return null;
      }
      var d = new Date(Date.parse(this.dueAt));
      return jsx(InfoBox, {
        mainText: this.renderDueDatePrimary(d),
        subText: this.renderDueDateSecondary(d)
      });
    }
  }, {
    key: "render",
    value: function render() {
      if (!this.hasInfoBoxContent && !this.props.styles.showEmptyInfoBox) {
        return null;
      }
      return jsx("div", {
        css: this.props.styles.container
      }, this.renderTimeLimit(), this.renderDueDate());
    }
  }]);
}(withI18nSupport(Component));
_BaseQuizInfo = BaseQuizInfo;
_defineProperty(BaseQuizInfo, "displayName", 'QuizInfo');
_defineProperty(BaseQuizInfo, "componentId", "Quizzes".concat(_BaseQuizInfo.displayName));
_defineProperty(BaseQuizInfo, "propTypes", {
  quiz: ImmutablePropTypes.record.isRequired,
  quizSession: ImmutablePropTypes.record,
  locale: PropTypes.string,
  timezone: PropTypes.string,
  hasSubmission: PropTypes.bool,
  styles: PropTypes.object
});
_defineProperty(BaseQuizInfo, "defaultProps", {
  locale: null,
  quizSession: null,
  timezone: null,
  hasSubmission: false
});
_defineProperty(BaseQuizInfo, "contextTypes", {
  locale: PropTypes.string,
  timezone: PropTypes.string
});
export var QuizInfo = withStyleOverrides(generateStyle, generateComponentTheme)(BaseQuizInfo);
export default QuizInfo;